import { Injectable } from '@angular/core'
import { KeyingAuthConfig } from '../configuration/keying.auth.config'
import { HttpClient } from '@angular/common/http'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { Auth0Client } from '@auth0/auth0-spa-js/dist/typings/Auth0Client'
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs'
import { createAuth0Client, IdToken } from '@auth0/auth0-spa-js'
import { catchError, concatMap, mergeMap, map, shareReplay, take, tap } from 'rxjs/operators'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { User } from '@overtur-monorepo/keying-user'

@Injectable({
  providedIn: 'root',
})
export class KeyingAuthService {
  _auth0Client$: Observable<Auth0Client>
  isAuthenticated$: Observable<boolean>
  handleRedirectCallback$: Observable<any>
  loggedIn: boolean
  _user = new BehaviorSubject<IdToken>(null)
  user$ = this._user.asObservable()
  _users = new BehaviorSubject<User[]>(null)
  users$ = this._users.asObservable()
  _keyingUser = new BehaviorSubject<User>(null)

  /* istanbul ignore next */
  constructor(
    private keyingAuthConfig: KeyingAuthConfig,
    private httpClient: HttpClient,
    private router: Router,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig
  ) {
    // Wrap Auth0 client creation to emit inside Angular zone
    this._auth0Client$ = this.wrapInZone(
      createAuth0Client({
        domain: this.keyingAuthConfig.domain,
        clientId: this.keyingAuthConfig.clientId,
        authorizationParams: {
          redirect_uri: this.keyingAuthConfig.redirectUrl,
          audience: this.keyingAuthConfig.audience,
        },
      })
    ).pipe(
      shareReplay(1),
      catchError((err) => throwError(() => err))
    )

    this.isAuthenticated$ = this._auth0Client$.pipe(
      concatMap((client: Auth0Client) => this.wrapInZone(client.isAuthenticated())),
      tap((res) => {
        this.loggedIn = res
      })
    )

    this.handleRedirectCallback$ = this._auth0Client$.pipe(
      concatMap((client: Auth0Client) => this.wrapInZone(client.handleRedirectCallback()))
    )

    this.user$
      .pipe(
        mergeMap((user) => {
          return user == null ? of(null as User) : this.createUser()
        })
      )
      .subscribe((user) => {
        if (user) {
          setTimeout(() => {
            this._keyingUser.next(user)
          }, 0)
        }
      })
  }

  /**
   * Wraps a Promise to ensure it resolves inside Angular's zone.
   * setTimeout is already patched by zone.js, so it will trigger change detection.
   */
  private wrapInZone<T>(promise: Promise<T>): Observable<T> {
    return new Observable<T>((subscriber) => {
      promise
        .then((value) => {
          // setTimeout is patched by zone.js and triggers change detection
          setTimeout(() => {
            subscriber.next(value)
            subscriber.complete()
          }, 0)
        })
        .catch((error) => {
          setTimeout(() => {
            subscriber.error(error)
          }, 0)
        })
    })
  }

  public get user() {
    return this._user.value
  }

  get keyingUser$() {
    return this._keyingUser.asObservable()
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    return this.isAuthenticated$.pipe(
      tap((loggedIn) => {
        if (!loggedIn) {
          this.login(state.url)
        }
      })
    )
  }

  /*istanbul ignore next*/
  private createUser(): Observable<User> {
    return this.httpClient
      .post<User>(this.keyingAppEnvironmentConfig.getUsersUrl(), null)
      .pipe(map((response) => User.createUser(response, response.id)))
  }

  login(redirectPath: string = '/') {
    this._auth0Client$.subscribe((client: Auth0Client) => {
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: this.keyingAuthConfig.redirectUrl,
        },
        appState: { target: redirectPath },
      })
    })
  }

  logout() {
    this._auth0Client$.subscribe((client: Auth0Client) => {
      client.logout({
        clientId: this.keyingAuthConfig.clientId,
        logoutParams: {
          returnTo: this.keyingAuthConfig.logoutUrl,
          federated: true,
        },
      })
    })
  }

  localAuthSetup() {
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          return this.getUser$()
        }
        return of(loggedIn)
      })
    )
    checkAuth$.subscribe()
  }

  handleAuthCallback() {
    const params = window.location.search
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string
      const authComplete$ = this.handleRedirectCallback$.pipe(
        tap((cbRes: any) => {
          targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/'
        }),
        concatMap(() => {
          return combineLatest([this.getUser$(), this.isAuthenticated$])
        })
      )
      authComplete$.subscribe((_) => {
        this.router.navigate([targetRoute])
      })
    }
  }

  getTokenSilently$(options?: any): Observable<string> {
    return this._auth0Client$.pipe(
      mergeMap((client: Auth0Client) => this.wrapInZone(client.getTokenSilently()))
    )
  }

  getUsers() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users`
    this.httpClient
      .get<User[]>(url)
      .pipe(take(1))
      .subscribe((users: User[]) => {
        this._users.next(users)
      })
  }

  private getUser$(options?: any): Observable<any> {
    return this._auth0Client$.pipe(
      concatMap((client: Auth0Client) => this.wrapInZone(client.getUser())),
      tap((user) => {
        setTimeout(() => {
          this._user.next(user)
        }, 0)
      })
    )
  }
}
